import type { Reservation, Table } from "@/store/types";
import { dayjs, parseTime } from "@/utils/date";
import { isDietaryTag, reservationTagLabel } from "@/lib/reservation-tags";

/** Pure helpers used by the day-report dashboard. They take the
 *  already-loaded reservation list for a given day plus, where
 *  relevant, the location's tables, and emit the numbers shown in
 *  ``OnTheBooksReport`` (pre-service) and ``ServiceRecapReport``
 *  (post-service). Industry naming follows OpenTable / SevenRooms
 *  conventions: covers, turn time, table turns, seat utilization,
 *  walk-in mix, party-size mix, etc. */

const SEATED_STATUSES: ReadonlySet<Reservation["status"]> = new Set(["sentado", "completada"]);

function median(values: number[]): number {
  if (values.length === 0) return 0;
  const sorted = values.toSorted((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
}

function mean(values: number[]): number {
  if (values.length === 0) return 0;
  return values.reduce((a, b) => a + b, 0) / values.length;
}

/** Total guests served — reservations that reached SEATED at least once
 *  (``sentado`` or ``completada``). The rest haven't put a single
 *  butt in a seat. */
export function computeCovers(reservations: Reservation[]): number {
  return reservations.filter((r) => SEATED_STATUSES.has(r.status)).reduce((s, r) => s + r.pax, 0);
}

/** Pax of any reservation that wasn't cancelled (covers + no-shows). */
export function computeOnBooksPax(reservations: Reservation[]): number {
  return reservations.filter((r) => r.status !== "cancelada").reduce((s, r) => s + r.pax, 0);
}

/** ``no_shows / (no_shows + seated + completed)`` — denominator excludes
 *  cancelled bookings since those didn't have a chance to no-show. */
export function computeNoShowRate(reservations: Reservation[]): number {
  const noShow = reservations.filter((r) => r.status === "noshow").length;
  const realised = reservations.filter((r) => SEATED_STATUSES.has(r.status)).length;
  const denom = noShow + realised;
  return denom === 0 ? 0 : noShow / denom;
}

/** ``cancelled / total``. Total includes every status — the cancellation
 *  rate is measured against every booking ever taken for the day. */
export function computeCancellationRate(reservations: Reservation[]): number {
  if (reservations.length === 0) return 0;
  const cancelled = reservations.filter((r) => r.status === "cancelada").length;
  return cancelled / reservations.length;
}

/** Walk-in covers as a fraction of total covers. */
export function computeWalkinMix(reservations: Reservation[]): { covers: number; mix: number } {
  const realised = reservations.filter((r) => SEATED_STATUSES.has(r.status));
  const total = realised.reduce((s, r) => s + r.pax, 0);
  const walkin = realised.filter((r) => r.origin === "walkin").reduce((s, r) => s + r.pax, 0);
  return { covers: walkin, mix: total === 0 ? 0 : walkin / total };
}

function dwellMinutes(r: Reservation): number | null {
  if (!SEATED_STATUSES.has(r.status)) return null;
  if (!r.seatedAt) return null;
  // ``time``/``endTime`` live in the location TZ already, ``seatedAt``
  // is a UTC ISO string. Parse both into the same calendar day so the
  // delta is just minutes.
  const seated = dayjs(r.seatedAt);
  const end = parseTime(r.endTime);
  // If parseTime produces a time on today's date and the seating spans
  // midnight, fall back to the raw difference between starts/end dur.
  const diff = end.valueOf() - seated.hour() * 60 * 60_000 - seated.minute() * 60_000;
  return Math.max(0, Math.round(diff / 60_000));
}

/** Average and median dwell time (turn time) in minutes, computed from
 *  ``seated_at → ends_at`` for reservations that reached the table. */
export function computeAvgTurnTime(reservations: Reservation[]): { mean: number; median: number } {
  const dwells: number[] = [];
  for (const r of reservations) {
    const d = dwellMinutes(r);
    if (d !== null) dwells.push(d);
  }
  return { mean: Math.round(mean(dwells)), median: Math.round(median(dwells)) };
}

/** Per-table reservation count (incl. cancelled? no — exclude cancelled
 *  since a table didn't actually turn for them). Returns ``avg`` and
 *  ``median`` over tables that saw at least one turn, plus the top 5
 *  tables by turn count. */
export function computeTableTurns(reservations: Reservation[]): {
  byTable: Map<string, number>;
  avg: number;
  median: number;
  top: Array<{ tableId: string; turns: number }>;
} {
  const byTable = new Map<string, number>();
  for (const r of reservations) {
    if (r.status === "cancelada") continue;
    for (const id of r.tableIds) {
      byTable.set(id, (byTable.get(id) ?? 0) + 1);
    }
  }
  const counts = [...byTable.values()];
  const top = [...byTable.entries()]
    .map(([tableId, turns]) => ({ tableId, turns }))
    .toSorted((a, b) => b.turns - a.turns)
    .slice(0, 5);
  return { byTable, avg: mean(counts), median: median(counts), top };
}

/** ``Σ(party_pax × dwell_min) / (Σ(table_max_capacity) × service_open_min)``.
 *  Returns 0 when the denominator is zero (no tables or service didn't
 *  open) to keep the dashboard from rendering NaN. */
export function computeSeatUtilization(
  reservations: Reservation[],
  tables: Table[],
  serviceOpenMinutes: number,
): number {
  if (tables.length === 0 || serviceOpenMinutes <= 0) return 0;
  const totalSeats = tables.reduce((s, t) => s + t.maxCapacity, 0);
  const seatMinutes = totalSeats * serviceOpenMinutes;
  if (seatMinutes <= 0) return 0;
  let consumed = 0;
  for (const r of reservations) {
    const d = dwellMinutes(r);
    if (d === null) continue;
    consumed += r.pax * d;
  }
  return consumed / seatMinutes;
}

/** Average minutes between booked time (``starts_at``) and ``seated_at``.
 *  Positive = guests waited; negative = sat early. Only counts
 *  reservations that actually got seated. */
export function computeAvgWaitToSeat(reservations: Reservation[]): number {
  const waits: number[] = [];
  for (const r of reservations) {
    if (!SEATED_STATUSES.has(r.status)) continue;
    if (!r.seatedAt) continue;
    const seated = dayjs(r.seatedAt);
    const startedAt = parseTime(r.time);
    // ``parseTime`` pins to today; recompose into the same calendar
    // day as ``seated`` so cross-midnight rounders don't blow up.
    const startedAligned = seated
      .hour(startedAt.hour())
      .minute(startedAt.minute())
      .second(0)
      .millisecond(0);
    waits.push(Math.round((seated.valueOf() - startedAligned.valueOf()) / 60_000));
  }
  return Math.round(mean(waits));
}

export type PartySizeBucket = "1-2" | "3-4" | "5-6" | "7+";

export function bucketPartySize(pax: number): PartySizeBucket {
  if (pax <= 2) return "1-2";
  if (pax <= 4) return "3-4";
  if (pax <= 6) return "5-6";
  return "7+";
}

/** Party-size mix across non-cancelled reservations (the mix is about
 *  the day's actual demand, not what the operator turned away). */
export function computePartySizeMix(
  reservations: Reservation[],
): Array<{ bucket: PartySizeBucket; count: number; pax: number }> {
  const buckets: Record<PartySizeBucket, { count: number; pax: number }> = {
    "1-2": { count: 0, pax: 0 },
    "3-4": { count: 0, pax: 0 },
    "5-6": { count: 0, pax: 0 },
    "7+": { count: 0, pax: 0 },
  };
  for (const r of reservations) {
    if (r.status === "cancelada") continue;
    const b = buckets[bucketPartySize(r.pax)];
    b.count += 1;
    b.pax += r.pax;
  }
  return (["1-2", "3-4", "5-6", "7+"] as PartySizeBucket[]).map((bucket) => ({
    bucket,
    ...buckets[bucket],
  }));
}

/** Covers per hour for the recap chart. Includes only realised covers
 *  (seated + completed) so the chart shows what we actually served. */
export function computeCoversPerHour(
  reservations: Reservation[],
): Array<{ hour: number; covers: number; count: number }> {
  const buckets = new Map<number, { covers: number; count: number }>();
  for (const r of reservations) {
    if (!SEATED_STATUSES.has(r.status)) continue;
    const hour = parseTime(r.time).hour();
    const b = buckets.get(hour) ?? { covers: 0, count: 0 };
    b.covers += r.pax;
    b.count += 1;
    buckets.set(hour, b);
  }
  return [...buckets.entries()].toSorted(([a], [b]) => a - b).map(([hour, v]) => ({ hour, ...v }));
}

/** Pacing for the on-the-books chart. Includes every non-cancelled row
 *  (covers projected, not yet realised), grouped per hour. */
export function computePacingPerHour(
  reservations: Reservation[],
): Array<{ hour: number; pax: number; count: number }> {
  const buckets = new Map<number, { pax: number; count: number }>();
  for (const r of reservations) {
    if (r.status === "cancelada") continue;
    const hour = parseTime(r.time).hour();
    const b = buckets.get(hour) ?? { pax: 0, count: 0 };
    b.pax += r.pax;
    b.count += 1;
    buckets.set(hour, b);
  }
  return [...buckets.entries()].toSorted(([a], [b]) => a - b).map(([hour, v]) => ({ hour, ...v }));
}

/** Hour with the most covers. ``null`` when nothing got served. */
export function computePeakHour(reservations: Reservation[]): number | null {
  const perHour = computeCoversPerHour(reservations);
  if (perHour.length === 0) return null;
  return perHour.reduce((max, row) => (row.covers > max.covers ? row : max)).hour;
}

/** Channel mix: count + covers per origin, descending. */
export function computeOriginMix(
  reservations: Reservation[],
): Array<{ origin: Reservation["origin"]; count: number; pax: number }> {
  const m = new Map<Reservation["origin"], { count: number; pax: number }>();
  for (const r of reservations) {
    if (r.status === "cancelada") continue;
    const cur = m.get(r.origin) ?? { count: 0, pax: 0 };
    cur.count += 1;
    cur.pax += r.pax;
    m.set(r.origin, cur);
  }
  return [...m.entries()]
    .map(([origin, v]) => ({ origin, ...v }))
    .toSorted((a, b) => b.count - a.count);
}

/** Tag distribution: how many reservations carry each tag. ``isDietary``
 *  separa lo que la cocina tiene que preparar de lo que solo describe a
 *  la mesa (bebé, mascota) — los reportes titulan su tarjeta
 *  "Restricciones" y esas dos no lo son. */
export function computeTagCounts(
  reservations: Reservation[],
): Array<{ tag: string; label: string; count: number; isDietary: boolean }> {
  const m = new Map<string, number>();
  for (const r of reservations) {
    if (r.status === "cancelada") continue;
    for (const tag of r.tags) m.set(tag, (m.get(tag) ?? 0) + 1);
  }
  return [...m.entries()]
    .map(([tag, count]) => ({
      tag,
      label: reservationTagLabel(tag),
      count,
      isDietary: isDietaryTag(tag),
    }))
    .toSorted((a, b) => b.count - a.count);
}

/** Average lead time in hours: ``starts_at - created_at``. Positive
 *  means guests booked ahead, ~0 means walk-ins / same-minute bookings. */
export function computeLeadTimeHours(reservations: Reservation[]): number {
  const leads: number[] = [];
  for (const r of reservations) {
    if (r.status === "cancelada") continue;
    if (!r.createdAt || r.createdAt === 0) continue;
    // ``r.time`` is local-day formatted; use the original ``date`` plus
    // ``time`` to produce the booked starts moment.
    const startsAt = parseTime(r.time);
    const startsAligned = dayjs(`${r.date} ${startsAt.format("HH:mm")}`).valueOf();
    if (startsAligned <= 0) continue;
    leads.push((startsAligned - r.createdAt) / (60 * 60_000));
  }
  return Math.round(mean(leads) * 10) / 10;
}

/** Concurrent occupancy curve at ``slotMinutes`` granularity for the
 *  on-the-books forecast. For each slot, counts reservations whose
 *  ``[starts_at, ends_at)`` interval covers it — that's how many
 *  parties are sitting AT that slot, not how many arrive. ``mesas`` is
 *  the count of distinct tableIds in use; ``pax`` sums the parties.
 *  ``pct`` is pax divided by the location's total seat capacity (so a
 *  full house reads close to 100 %).
 *
 *  Walks the day from the earliest ``time`` to the latest ``endTime``,
 *  rounded out to the slot grid. Cancelled reservations are excluded.
 *  Walk-ins and seated parties are included since they consume floor
 *  space too. */
export function computeLoadCurve(
  reservations: Reservation[],
  tables: Table[],
  slotMinutes = 15,
): Array<{
  slot: string;
  minutesFromMidnight: number;
  mesas: number;
  pax: number;
  pct: number;
}> {
  const active = reservations.filter((r) => r.status !== "cancelada");
  if (active.length === 0) return [];

  const totalSeats = tables.reduce((s, t) => s + t.maxCapacity, 0);

  const intervals = active.map((r) => ({
    start: parseTime(r.time).hour() * 60 + parseTime(r.time).minute(),
    end: parseTime(r.endTime).hour() * 60 + parseTime(r.endTime).minute(),
    pax: r.pax,
    tableIds: r.tableIds,
  }));

  // ``endTime`` of "12:00 AM" parses to minute 0 — bump it to 24h so
  // late-night services fall on the right side of every slot below.
  for (const i of intervals) {
    if (i.end <= i.start) i.end += 24 * 60;
  }

  const minStart = Math.min(...intervals.map((i) => i.start));
  const maxEnd = Math.max(...intervals.map((i) => i.end));
  const firstSlot = Math.floor(minStart / slotMinutes) * slotMinutes;
  const lastSlot = Math.ceil(maxEnd / slotMinutes) * slotMinutes;

  const out = [];
  for (let m = firstSlot; m < lastSlot; m += slotMinutes) {
    const slotEnd = m + slotMinutes;
    let pax = 0;
    const tableSet = new Set<string>();
    for (const i of intervals) {
      if (i.start < slotEnd && i.end > m) {
        pax += i.pax;
        for (const t of i.tableIds) tableSet.add(t);
      }
    }
    const hour = Math.floor(m / 60) % 24;
    const minute = m % 60;
    out.push({
      slot: `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`,
      minutesFromMidnight: m,
      mesas: tableSet.size,
      pax,
      pct: totalSeats > 0 ? pax / totalSeats : 0,
    });
  }
  return out;
}

/** Confirmed vs unconfirmed split for the on-the-books KPI tile. */
export function computeConfirmation(reservations: Reservation[]): {
  confirmed: number;
  pending: number;
} {
  let confirmed = 0;
  let pending = 0;
  for (const r of reservations) {
    if (r.status === "cancelada") continue;
    if (r.isConfirmedByHost) confirmed += 1;
    else pending += 1;
  }
  return { confirmed, pending };
}

/** Format a fraction in ``0..1`` as ``XX%``. */
export function formatPct(value: number): string {
  return `${Math.round(value * 100)}%`;
}

export function formatHourLabel(hour: number): string {
  const ampm = hour >= 12 ? "PM" : "AM";
  const h = hour % 12 || 12;
  return `${h} ${ampm}`;
}

export function formatMinutesAsHm(minutes: number): string {
  if (!Number.isFinite(minutes) || minutes <= 0) return "—";
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}
